import {PathSegment} from '@/models/path-segment';
import {Journey} from '@/models/journey';
import {JourneyPartType} from '@/models/journey-part-type';
import {SimplePathSegment} from '@/models/simple-path-segment';
import {PackagePathSegment} from '@/models/package-path-segment';
import {AdditionalOptionsPathSegment} from '@/models/additional-options-path-segment';
import {Option} from '@/models/option';
import {OptionGroup} from '@/models/option-group';
import {MorticianSettings} from '@/models/mortician-settings';
import {HeadlinePathSegment} from '@/models/headline-path-segment';
import { AxiosResponse } from 'axios';


/**
 * @class Mapper
 * @classdesc The Mapper class maps incoming API Transfer objects to internal types. It also builds the path segment tree.
 */
export class Mapper
{
    private static dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/;
    private static replacePattern = [
        {
            pattern: /\{\{br\}\}/ig,
            replacement : '<br>'
        },
        {
            pattern: /\[nbsp\]/ig,
            replacement : '&nbsp;'
        },
        {
            pattern: /\[-\]/ig,
            replacement : '&shy;'
        }
    ]

    constructor(
        private journeys: Array<Journey>,
        private options: Array<Option>,
        private optionGroups: Array<OptionGroup>)
    {
    }

    public static parseJourneyResponse(resp: AxiosResponse): Journey[]
    {
        return resp.data.map((data: any) => {
            for (const [key] of Object.entries(data)) {
                if (typeof data[key] === 'string' && this.dateFormat.test(data[key])) {
                    data[key] = new Date(Date.parse(data[key]));
                }
                if (typeof data[key] === 'string' && data[key].length) {
                    data[key] = Mapper.replaceContaoPlaceholders(data[key]);
                }
            }
            return data;
        }) as Journey[];
    }

    public static parseOptionResponse(resp: AxiosResponse): Option[]
    {
        return resp.data.map((data: any) => {
            for (const [key] of Object.entries(data)) {
                if (typeof data[key] === 'string' && this.dateFormat.test(data[key])) {
                    data[key] = new Date(Date.parse(data[key]));
                }
                if (typeof data[key] === 'string' && data[key].length) {
                    data[key] = Mapper.replaceContaoPlaceholders(data[key]);
                }
            }
            return data;
        }) as Option[];
    }

    public static parseOptionGroupResponse(resp: AxiosResponse): OptionGroup[]
    {
        return resp.data.map((data: any) => {
            for (const [key] of Object.entries(data)) {
                if (typeof data[key] === 'string' && this.dateFormat.test(data[key])) {
                    data[key] = new Date(Date.parse(data[key]));
                }
                if (typeof data[key] === 'string' && data[key].length) {
                    data[key] = Mapper.replaceContaoPlaceholders(data[key]);
                }
            }
            return data;
        }) as OptionGroup[];
    }

    public static replaceContaoPlaceholders(data: string): string {
        this.replacePattern.forEach(p => {
            data = data.replace(p.pattern, p.replacement);
        });
        return data;
    }

    /**
     * Parses the mortician settings from the given string into a @{MorticianSettings} object.
     * @param json The json string to parse
     */
    public static parseMorticianSettings(json: string): MorticianSettings
    {
        return JSON.parse(json);
    }

    /**
     * Builds and returns the path segments tree from the array of journeys.
     */
    public buildPathSegmentTree(fcbId: number): Array<PathSegment>
    {
        const pathSegments: Array<PathSegment> = this.journeys.filter(j => fcbId ? j.pid === fcbId : j.type === 'root').map(j => this.extractPathSegmentFromJourney(j))
        
        // recursively map the rest
        for (const pathSegment of pathSegments) {
            this.recursivelyMapChildSegments(pathSegment);
        }

        return pathSegments;
    }

    /**
     * Maps an array of OptionGroup IDs to their corresponding instances
     * @param optionGroupIds
     */
    public mapOptionGroups(optionGroupIds: Array<number>): Array<OptionGroup>
    {
        return this.optionGroups.filter(ogroup => optionGroupIds.includes(ogroup.id));
    }

    /**
     * Maps an array of Option IDs to their corresponding instances
     * @param optionIds
     */
    public mapOptions(optionIds: Array<number>): Array<Option>
    {
        if (!optionIds || optionIds[0] === 0) {
            return [];
        }

        return optionIds.map((id) => {
            return this.options.find((option: Option) => !!option && option.id === id) as Option;
        });
    }

    /**
     * Recursively maps journeys to the path segments
     * @param pathSegment
     */
    private recursivelyMapChildSegments(pathSegment: PathSegment): void
    {
        const id = pathSegment.dataObject?.id as number;

        for (const journey of this.journeys.filter(j => j.pid === id)) {
            const innerPathSegment = this.extractPathSegmentFromJourney(journey);
            pathSegment.children.push(innerPathSegment);
            this.recursivelyMapChildSegments(innerPathSegment);
        }
    }

    /**
     * Extracts a path segment without option or optionGroup resolution from a journey transfer object for testing
     *
     * @param journey the journey to extract
     */
    public extractPathSegmentFromJourneyShallow(journey: Journey): PathSegment
    {
        let pathSegment: PathSegment;

        switch (journey.type)
        {
            case JourneyPartType.Path:
                const simplePathSegment = new SimplePathSegment(
                    'SimplePathSegment',
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.journeyDescription,
                    journey.image,
                    journey.icon,
                    journey
                );
                pathSegment = simplePathSegment;
                break;
            case JourneyPartType.Package:
                const packagePathSegment = new PackagePathSegment(
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.packageRecommended,
                    journey.packageDescription,
                    journey.image,
                    journey.icon,
                    [],
                    journey
                );
                pathSegment = packagePathSegment;
                break;
            case JourneyPartType.AdditionalOptions:
                const additionalOptionsPathSegment = new AdditionalOptionsPathSegment(
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    [],
                    journey
                );
                pathSegment = additionalOptionsPathSegment;
                break;
            case JourneyPartType.Headline:
                const headlineSegment = new HeadlinePathSegment(
                    journey.title,
                    journey.alias,
                    journey.headlineType,
                    journey.headlineContent,
                    journey,
                    journey.headlineAlignment
                );
                pathSegment = headlineSegment;
                break;
            case JourneyPartType.Root:
                const rootPathSegment = new SimplePathSegment(
                    'SimplePathSegment',
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.journeyDescription,
                    journey.image,
                    journey.icon,
                    journey
                );
                pathSegment = rootPathSegment;
                break;
        }

        return pathSegment;
    }

    /**
     * Extracts a path segment from a journey transfer object
     * @param journey the journey to extract
     */
    private extractPathSegmentFromJourney(journey: Journey): PathSegment
    {
        let pathSegment: PathSegment;

        switch (journey.type)
        {
            case JourneyPartType.Path:
                const simplePathSegment = new SimplePathSegment(
                    'SimplePathSegment',
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.journeyDescription,
                    journey.image,
                    journey.icon,
                    journey
                );
                pathSegment = simplePathSegment;
                break;
            case JourneyPartType.Package:
                const packagePathSegment = new PackagePathSegment(
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.packageRecommended,
                    journey.packageDescription,
                    journey.image,
                    journey.icon,
                    this.mapOptions(journey.packageOptions),
                    journey
                );
                pathSegment = packagePathSegment;
                break;
            case JourneyPartType.AdditionalOptions:
                const additionalOptionsPathSegment = new AdditionalOptionsPathSegment(
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    this.mapOptionGroups(journey.optionGroups),
                    journey
                );
                pathSegment = additionalOptionsPathSegment;
                break;
            case JourneyPartType.Headline:
                const headlineSegment = new HeadlinePathSegment(
                    journey.title,
                    journey.alias,
                    journey.headlineType,
                    journey.headlineContent,
                    journey,
                    journey.headlineAlignment
                );
                pathSegment = headlineSegment;
                break;
            case JourneyPartType.Root:
                const rootPathSegment = new SimplePathSegment(
                    'SimplePathSegment',
                    journey.title,
                    journey.alias,
                    journey.price,
                    journey.isPriceVisible,
                    journey.isCostExternal,
                    journey.journeyDescription,
                    journey.image,
                    journey.icon,
                    journey
                );
                pathSegment = rootPathSegment;
                break;
        }

        return pathSegment;
    }
}
